// Unified pointer input for canvas immediate-mode UI. Mouse + touch collapse to
// a single "click" per frame that scenes hit-test against their button rects.
// Includes the iOS double-tap-zoom guard required by the mobile brief.

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function pointInRect(px: number, py: number, r: Rect): boolean {
  return px >= r.x && px <= r.x + r.w && py >= r.y && py <= r.y + r.h;
}

export class Input {
  /** Current pointer position in CSS pixels (canvas-relative). */
  x = 0;
  y = 0;
  down = false;

  private pending: { x: number; y: number } | null = null;
  private active: { x: number; y: number } | null = null;
  private lastTouchEnd = 0;
  /** Clicks promoted before this timestamp are discarded (scene-switch guard). */
  private suppressUntil = 0;

  constructor(private el: HTMLElement) {
    el.addEventListener("pointermove", this.onMove, { passive: true });
    el.addEventListener("pointerdown", this.onDown, { passive: true });
    el.addEventListener("pointerup", this.onUp, { passive: true });
    el.addEventListener("pointercancel", this.onCancel, { passive: true });
    // Double-tap-zoom guard: swallow a second touchend inside 300ms.
    el.addEventListener("touchend", this.onTouchEnd, { passive: false });
  }

  private rel(e: PointerEvent): { x: number; y: number } {
    const r = this.el.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  private onMove = (e: PointerEvent): void => {
    const p = this.rel(e);
    this.x = p.x;
    this.y = p.y;
  };

  private onDown = (e: PointerEvent): void => {
    const p = this.rel(e);
    this.x = p.x;
    this.y = p.y;
    this.down = true;
  };

  private onUp = (e: PointerEvent): void => {
    const p = this.rel(e);
    this.x = p.x;
    this.y = p.y;
    this.down = false;
    this.pending = p;
  };

  private onCancel = (): void => {
    this.down = false;
  };

  private onTouchEnd = (e: TouchEvent): void => {
    const now = performance.now();
    if (now - this.lastTouchEnd <= 300) e.preventDefault();
    this.lastTouchEnd = now;
  };

  /**
   * Drops any queued/live click and ignores clicks promoted for the next `ms`
   * milliseconds. Called on scene switches so a double-tap's second click (or
   * any click queued across the switch) cannot fall through onto whatever
   * button happens to occupy the same spot in the NEW scene.
   */
  suppressFor(ms: number): void {
    this.pending = null;
    this.active = null;
    this.suppressUntil = performance.now() + ms;
  }

  /** Called once at the top of a frame to promote a queued click. */
  beginFrame(): void {
    if (this.pending && performance.now() < this.suppressUntil) {
      this.pending = null; // click-through guard (see suppressFor)
    }
    if (this.active === null && this.pending) {
      this.active = this.pending;
      this.pending = null;
    }
  }

  /** Called after logic ran to retire the consumed/expired click. */
  endFrame(ran: boolean): void {
    if (ran) this.active = null;
  }

  /** True (and consumes the click) if a click landed inside `r` this frame. */
  clicked(r: Rect): boolean {
    if (this.active && pointInRect(this.active.x, this.active.y, r)) {
      this.active = null;
      return true;
    }
    return false;
  }

  /** True if a click landed anywhere this frame (consumes it). */
  clickedAnywhere(): boolean {
    if (this.active) {
      this.active = null;
      return true;
    }
    return false;
  }

  hover(r: Rect): boolean {
    return pointInRect(this.x, this.y, r);
  }
}
